import { UserAccessHandler } from '@sennder/shell-utilities'
import { featureFlag } from '@/services/featureFlagProvider'
import { getStateData } from '@/store'

/**
 * Access handlers are functions which return a boolean. They can be used as menu visibility handlers, and called in route guards.
 *
 * NAMING CONVENTIONS
 *
 * Access handler name should be a sentence which describes what access it checks, or user role it checks.
 *
 * - userIs{Role}: Check if the user has a specific role
 * - canAccess{Page}: Check if the user has access to the page
 */

/**
 * A user is limited to a set of facilities when they have one or more facility
 * identifiers assigned. Such users may only access the Operations Overview; this
 * is enforced centrally by the navigation guard in `src/router/routes.ts`, and
 * the sidebar is hidden for them. Users without any assigned facilities are
 * unaffected and keep full access.
 */
export const hasLimitedFacilities = (): boolean => {
  const facilities = getStateData().user?.visibleFacilities
  return !!facilities && facilities.length > 0
}

export const canAccessNewShipment: UserAccessHandler = () =>
  !!featureFlag.getFeatureFlag('load-booking')

export const canAccessOpportunities: UserAccessHandler = () =>
  !!featureFlag.getFeatureFlag('opportunities')

export const canAccessMyAnalytics: UserAccessHandler = () =>
  !!featureFlag.getFeatureFlag('my-analytics')

// Facility-limited users may always reach the Operations Overview (it is their
// only page), regardless of the feature flag.
export const canAccessOperationsOverview: UserAccessHandler = () =>
  hasLimitedFacilities() || !!featureFlag.getFeatureFlag('operations-dashboard')

export const canAccessMyCargos: UserAccessHandler = () =>
  !!featureFlag.getFeatureFlag('my-cargos')
